import { Box, Button, Typography, useTheme } from '@mui/material'
import { memo, useCallback, useMemo, useRef } from 'react'
import StrikeTooltipOiBar from './StrikeTooltipOiBar'
import OutlookDisplay from './OutlookDisplay'
import {
    CustomOptionSecurityType,
    optionGreeksCols,
    optionTradeDataType,
    OutlookToName
} from '../../pages/typings/options.typings'
import { Exchange } from '../../proto/generated/common'
import { selectPrimarySecurity } from '../../selectors/home'
import { useSelector } from 'react-redux'
import { TransactionType } from '../../proto/generated/order'
import { Outlook, outlookToJSON } from '../../proto/generated/candle-data-depot-option-chain'
import { getSecurityKey } from '../../util/security'
import ROUTES from '../../assets/routes'
import BuySellButton from '../common/buySellButton/buySellButton'
import { formatGreeks, greekColumns } from '../../pages/home/optionChainViewTabs/components/DynamicColumnRender'
import './style.css'
import { OIChange } from '../../pages/typings/options.typings'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import {
    selectCandleChangeLtp,
    selectCandleChangeLtpChange,
    selectCandleChangeOpenInterest,
    selectCandleChangeOpenInterestChange
} from '../../selectors/security'
import { selectShowOptionChainQuantityInLots } from '../../selectors/common'
import { generatePath } from 'react-router-dom'
import { Security } from '../../proto/generated/security'
import { formatLargeNumber } from '../../util/screener'
import Chart from '../../assets/icons/Chart'
import { buyColor, red } from '../../assets/colors'
import { getStrikePopoverSurfaceSx } from '../../pages/home/optionChainViewTabs/components/strikePopoverStyles'

type Props = {
    record: optionTradeDataType
    roundStrike: boolean
    partial: 'call' | 'put' | 'none'
    keyValueType: boolean
    showBuySellButtons?: boolean
    onBuySellButtonClick?: (type: 'buy' | 'sell', callput: 'call' | 'put') => void
}

const renderGreekSymbol = (col: string) => {
    switch (col) {
        case 'delta':
            return 'δ'
        case 'gamma':
            return 'Γ'
        case 'vega':
            return 'ν'
        case 'theta':
            return 'Θ'
        case 'rho':
            return 'ρ'
        default:
            return ''
    }
}

const formatSignedChange = (value: number | undefined, decimals = 2): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '-'
    if (value === 0) return '0'
    const formatted = Math.abs(value).toFixed(decimals)
    return value > 0 ? `+${formatted}` : `-${formatted}`
}

const StrikeTooltip = memo((props: Props) => {
    const {
        record,
        roundStrike,
        partial,
        keyValueType = false,
        showBuySellButtons = false,
        onBuySellButtonClick = () => {}
    } = props
    const theme = useTheme()
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const security = expressionMode ? null : underlying || primary

    const strikeDisplay = useMemo(
        () =>
            roundStrike
                ? (record.strikeCol.strike / (security?.priceMultiplier ?? 1))?.toFixed(0)
                : (record.strikeCol.strike / (security?.priceMultiplier ?? 1))?.toFixed(2),
        [record.strikeCol.strike, roundStrike, security?.priceMultiplier]
    )

    const maxPainDisplay = useMemo(
        () =>
            roundStrike
                ? ((record.strikeCol?.maxPain ?? 0) / (security?.priceMultiplier ?? 1))?.toFixed(0)
                : ((record.strikeCol?.maxPain ?? 0) / (security?.priceMultiplier ?? 1))?.toFixed(2),
        [record.strikeCol?.maxPain, roundStrike, security?.priceMultiplier]
    )

    const gridBorderClass = useMemo(() => {
        if (partial === 'call') return 'strike-tooltip-grid--call'
        if (partial === 'put') return 'strike-tooltip-grid--put'
        return 'strike-tooltip-grid--both'
    }, [partial])

    const gridBorderSx = useMemo(() => {
        const callColor = theme.palette.call.main
        const putColor = theme.palette.put.main
        if (partial === 'call') {
            return { borderLeftColor: callColor }
        }
        if (partial === 'put') {
            return { borderRightColor: putColor }
        }
        return { borderLeftColor: callColor, borderRightColor: putColor }
    }, [partial, theme.palette.call.main, theme.palette.put.main])

    const surfaceSx = useMemo(() => getStrikePopoverSurfaceSx(theme), [theme])

    return (
        <Box className="strike-tooltip-root">
            {!keyValueType && (
                <Box
                    className="strike-tooltip-header"
                    sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ...surfaceSx }}
                >
                    <Metric label="Strike" value={strikeDisplay} />
                    <Metric label="PCR" value={record.strikeCol.pcr?.toFixed(2) ?? '-'} />
                    <Metric label="Max Pain" value={maxPainDisplay} />
                </Box>
            )}
            <Box
                className={`strike-tooltip-grid ${gridBorderClass}`}
                sx={{ ...surfaceSx, ...gridBorderSx }}
            >
                {(partial === 'none' || partial === 'call') && (
                    <OptionSidePanel
                        side="call"
                        option={record.callOption}
                        record={record}
                        roundStrike={roundStrike}
                        keyValueType={keyValueType}
                    />
                )}
                {(partial === 'none' || partial === 'put') && (
                    <OptionSidePanel
                        side="put"
                        option={record.putOption}
                        record={record}
                        roundStrike={roundStrike}
                        keyValueType={keyValueType}
                    />
                )}
            </Box>
            {showBuySellButtons && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', pt: 1, pl: 1 }}>
                    <BuySellButton
                        transactionType={TransactionType.BUY}
                        onClick={() => {
                            AnalyticsManager.logEvent('Buy Clicked', {
                                source: 'option-chain-tooltip',
                                transaction_type: 'Buy',
                                type: partial === 'call' ? 'call' : 'put'
                            })
                            onBuySellButtonClick('buy', partial === 'call' ? 'call' : 'put')
                        }}
                        sx={{ mr: 1, minWidth: '24px' }}
                        size="small"
                        variant="contained"
                    >
                        Buy
                    </BuySellButton>
                    <BuySellButton
                        transactionType={TransactionType.SELL}
                        onClick={() => {
                            AnalyticsManager.logEvent('Buy Clicked', {
                                source: 'option-chain-tooltip',
                                transaction_type: 'Sell',
                                type: partial === 'call' ? 'call' : 'put'
                            })
                            onBuySellButtonClick('sell', partial === 'call' ? 'call' : 'put')
                        }}
                        sx={{ minWidth: '24px' }}
                        size="small"
                        variant="contained"
                    >
                        Sell
                    </BuySellButton>
                </Box>
            )}
        </Box>
    )
})

const Metric = memo(({ label, value }: { label: string; value: string }) => (
    <Box>
        <Typography component="span" className="strike-tooltip-row-label" color="text.secondary">
            {label}{' '}
        </Typography>
        <Typography component="span" className="strike-tooltip-row-value">
            {value}
        </Typography>
    </Box>
))

type OptionSidePanelProps = {
    side: 'call' | 'put'
    option: CustomOptionSecurityType | undefined
    record: optionTradeDataType
    roundStrike: boolean
    keyValueType: boolean
}

const OptionSidePanel = memo(({ side, option, record, roundStrike, keyValueType }: OptionSidePanelProps) => {
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const security = expressionMode ? null : underlying || primary
    const isCall = side === 'call'

    const handleOpenChart = useCallback(() => {
        AnalyticsManager.logEvent(isCall ? 'Call Chart Clicked' : 'Put Chart Clicked', {
            source: 'option-chain-tooltip'
        })
        const key = getSecurityKey(option?.security?.exchange ?? Exchange.EXCHANGE_UNKNOWN, option?.security?.code ?? '')
        window.open(generatePath(ROUTES.symbol, { symbol: key }), '_blank')
    }, [isCall, option?.security])

    const oiChangeAmt = option?.oiChanges?.oiChange?.change
    const openInterest = option?.openInterest ?? 0
    const oiRankVal = option?.oiRank ?? 0

    /** Same buy/red as option chain and index snapshot cells. */
    const ltpChangeColor = useMemo(() => {
        const change = option?.ltpChange
        if (!change || change === 0) return 'text.secondary'
        return change > 0 ? buyColor : red
    }, [option?.ltpChange])

    const oiChangeColor = useMemo(() => {
        const change = option?.oiChanges?.oiChange?.change
        if (!change || change === 0) return 'text.primary'
        return change > 0 ? buyColor : red
    }, [option?.oiChanges?.oiChange?.change])

    const strikeLabel = useMemo(
        () =>
            roundStrike
                ? (record.strikeCol.strike / (security?.priceMultiplier ?? 1))?.toFixed(0)
                : (record.strikeCol.strike / (security?.priceMultiplier ?? 1))?.toFixed(2),
        [record.strikeCol.strike, roundStrike, security?.priceMultiplier]
    )

    const priceRowLabel = keyValueType ? `${strikeLabel} ${isCall ? 'CE' : 'PE'}` : 'Price'

    return (
        <Box className="strike-tooltip-panel">
            <DataRow label={priceRowLabel}>
                <Box className="strike-tooltip-price-row">
                    <Box className="strike-tooltip-price-values">
                        <Typography className="strike-tooltip-row-value" component="span" noWrap>
                            <RenderTooltiptableValues record={option} index="ltp" />
                        </Typography>
                        <Typography className="strike-tooltip-row-value" component="span" noWrap sx={{ color: ltpChangeColor }}>
                            <RenderTooltiptableValues record={option} index="ltpChange" signed />
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        color={isCall ? 'call' : 'put'}
                        size="small"
                        onClick={handleOpenChart}
                        className="strike-tooltip-chart-btn"
                        disableRipple
                        startIcon={
                            <Chart
                                sx={{
                                    fontSize: 16,
                                    '& path': {
                                        strokeWidth: 1.5,
                                    },
                                }}
                            />
                        }
                    >
                        {isCall ? 'CE' : 'PE'}
                    </Button>
                </Box>
            </DataRow>

            <DataRow label="Implied Volatility">
                <Typography className="strike-tooltip-row-value strike-tooltip-single-value" component="span" noWrap>
                    <IvValue record={option} />
                </Typography>
            </DataRow>

            <DataRow label="Open Interest">
                <Box className="strike-tooltip-oi-row">
                    <Typography className="strike-tooltip-row-value" component="span" noWrap sx={{ flexShrink: 0 }}>
                        <RenderTooltiptableValues record={option} index="openInterest" />
                    </Typography>
                    {openInterest !== 0 && (
                        <StrikeTooltipOiBar
                            oiRank={oiRankVal}
                            openInterest={openInterest}
                            oiChangeRaw={oiChangeAmt}
                        />
                    )}
                </Box>
            </DataRow>

            <DataRow label="OI Change">
                <Typography className="strike-tooltip-row-value strike-tooltip-single-value" component="span" noWrap sx={{ color: oiChangeColor }}>
                    <RenderTooltiptableValues record={option} index="oiChange" signed />
                </Typography>
            </DataRow>

            <DataRow label="Greeks">
                <Box className="strike-tooltip-greeks">
                    {optionGreeksCols.map((col) => (
                        <Box key={col} className="strike-tooltip-greek-item">
                            <Typography component="span" className="strike-tooltip-greek-symbol">
                                {renderGreekSymbol(col)}
                            </Typography>
                            <Typography component="span" className="strike-tooltip-row-value" noWrap>
                                <RenderTooltiptableValues record={option} index={col} />
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </DataRow>

            <DataRow label="Outlook">
                <OutlookDisplay record={option} outlook={option?.oiChanges?.outlook} />
            </DataRow>
        </Box>
    )
})

const IvValue = memo(({ record }: { record: CustomOptionSecurityType | undefined }) => {
    const iv = record?.impliedVolatility
    if (iv === undefined || iv === null || iv === 0) return <>-</>
    return <>{(iv * 100).toFixed(2)}%</>
})

const DataRow = memo(({ label, children }: { label: string; children: ReactNode }) => (
    <Box className="strike-tooltip-row">
        <Typography className="strike-tooltip-row-label" color="text.secondary" noWrap>
            {label}
        </Typography>
        <Box className="strike-tooltip-row-content">{children}</Box>
    </Box>
))

export default memo(StrikeTooltip)

export const RenderTooltiptableValues = memo(({
    record,
    index,
    hidePercent = false,
    signed = false
}: {
    record: any
    index: string
    hidePercent?: boolean
    signed?: boolean
}) => {
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const security = expressionMode ? null : underlying || primary
    const changeData: OIChange = record?.oiChanges
    const format = ['openInterest', 'oiChange', 'volume'].includes(index)
    let returnValue = record?.[index] ?? '-'

    if (index === 'openInterest') {
        return <RenderTooltipOpenInterest oiPassed={Number(record?.[index])} security={record.security} />
    }
    if (index === 'oiChange') {
        return (
            <RenderTooltipOpenInterestChange
                oiPassed={Number(record?.oiChanges?.oiChange?.change ?? 0)}
                security={record.security}
                signed={signed}
            />
        )
    }
    if (index === 'outlook') {
        returnValue = [Outlook.OUTLOOK_UNKNOWN, Outlook.UNRECOGNIZED, undefined].includes(changeData?.outlook)
            ? '-'
            : OutlookToName[outlookToJSON(Number(changeData?.outlook))]
    } else if (greekColumns.includes(index)) {
        if (index === 'gamma') {
            returnValue = record?.optionGreeks?.[index] ? Number(record?.optionGreeks?.[index]).toFixed(4) : '-'
        } else {
            returnValue = record?.optionGreeks?.[index] ? formatGreeks(Number(record?.optionGreeks?.[index])) : '-'
        }
    }
    if (format && returnValue !== '-') {
        returnValue = formatLargeNumber(returnValue)
    }
    if (index === 'oiChange' && hidePercent) {
        const changepercent = changeData?.oiChange?.changePercent
        if (changepercent) {
            returnValue = `${returnValue} (${changepercent}%)`
        }
    }
    if (index === 'ltp' || index === 'ltpChange') {
        if (index === 'ltp') {
            return (
                <RenderTooltipLtp
                    ltpPassed={Number(record[index])}
                    security={record.security}
                    priceMultiplier={security?.priceMultiplier ?? 100}
                />
            )
        }
        return (
            <RenderTooltipLtpChange
                ltpPassed={Number(record[index])}
                security={record.security}
                priceMultiplier={security?.priceMultiplier ?? 100}
                signed={signed}
            />
        )
    }
    if (returnValue !== '-' && (index === 'impliedVolatility' || index === 'ltpChangePercent')) {
        if (index === 'impliedVolatility') {
            returnValue = (returnValue * 100)?.toFixed(2)
        } else {
            returnValue = returnValue?.toFixed(2)
        }
    }
    if (returnValue === 0) returnValue = '-'
    if (signed && typeof returnValue === 'number') {
        return formatSignedChange(returnValue)
    }
    return returnValue
})

const RenderTooltipLtp = memo(({ security, priceMultiplier, ltpPassed }: {
    security: Security | null
    priceMultiplier: number
    ltpPassed: number
}) => {
    const firstRender = useRef(false)
    const secKey = getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const ltp = useSelector(selectCandleChangeLtp(secKey))
    const ltpFinal = useMemo(() => {
        if (!ltp) return ltpPassed
        firstRender.current = true
        return ltp
    }, [ltp, ltpPassed])
    return <>{((ltpFinal ?? 0) / priceMultiplier).toFixed(2)}</>
})

const RenderTooltipLtpChange = memo(({ security, priceMultiplier, ltpPassed, signed }: {
    security: Security | null
    priceMultiplier: number
    ltpPassed: number
    signed?: boolean
}) => {
    const firstRender = useRef(false)
    const secKey = getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const ltp = useSelector(selectCandleChangeLtpChange(secKey))
    const ltpFinal = useMemo(() => {
        if (!ltp) return ltpPassed
        firstRender.current = true
        return ltp
    }, [ltp, ltpPassed])
    const display = (ltpFinal ?? 0) / priceMultiplier
    if (signed) {
        return <>{formatSignedChange(display)}</>
    }
    return <>{display.toFixed(2)}</>
})

const RenderTooltipOpenInterest = memo(({ security, oiPassed }: { security: Security | undefined; oiPassed: number }) => {
    const firstRender = useRef(false)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const secKey = getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const oi = useSelector(selectCandleChangeOpenInterest(secKey))
    const oiFinal = useMemo(() => {
        if (!oi) return oiPassed
        firstRender.current = true
        return oi
    }, [oi, oiPassed])
    return (
        <>
            {oiFinal
                ? quantityInLots
                    ? formatLargeNumber(oiFinal / (security?.lotSize ?? 50))
                    : formatLargeNumber(oiFinal)
                : '-'}
        </>
    )
})

const RenderTooltipOpenInterestChange = memo(({ security, oiPassed, signed }: {
    security: Security | null
    oiPassed: number
    signed?: boolean
}) => {
    const firstRender = useRef(false)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const secKey = getSecurityKey(security?.exchange ?? 0, security?.code ?? '')
    const oi = useSelector(selectCandleChangeOpenInterestChange(secKey))
    const oiFinal = useMemo(() => {
        if (!oi) return oiPassed
        firstRender.current = true
        return oi
    }, [oi, oiPassed])
    const formatted = oiFinal
        ? quantityInLots
            ? formatLargeNumber(oiFinal / (security?.lotSize ?? 50))
            : formatLargeNumber(oiFinal)
        : '-'
    if (signed && oiFinal) {
        const absFormatted = formatted.startsWith('-') ? formatted.slice(1) : formatted
        if (oiFinal > 0) return <>+{absFormatted}</>
        if (oiFinal < 0) return <>-{absFormatted}</>
    }
    return <>{formatted}</>
})
